import React, { createContext, ReactNode } from 'react';
import * as vp from 'vopay';
import useStorage from '../hooks/useStorage';

export type BankCtx = {
  FinancialInstitution: vp.FinancialInstitutionInfo | undefined;
  MainFinancialInstitution: vp.FinancialInstitutionInfo | undefined;
  BankingType: 'online' | 'manual' | undefined;
  AccountType: 'personal' | 'business' | undefined;
  SubFiniancialIntistutions: vp.FinancialInstitutionInfo[] | undefined;
  BankAccountRail: vp.BankingPaymentRail | undefined;
  setFinancialInstitution: (institution: vp.FinancialInstitutionInfo) => void;
  setMainFinancialInstitution: (institution: vp.FinancialInstitutionInfo) => void;
  setBankingType: (Type: 'online' | 'manual' | undefined) => void;
  setAccountType: (Type: 'personal' | 'business' | undefined) => void;
  setSubFiniancialIntistutions: (institutions: vp.FinancialInstitutionInfo[]) => void;
  setBankAccountRail: (rail: vp.BankingPaymentRail) => void;
  reset: () => void;
};

export const BankContext = createContext<BankCtx>({
  FinancialInstitution: {
    AssociatedFINames: '',
    AssociatedFINamesFrench: '',
    AssociatedExternalFIIDs: '',
    ColorCode: '',
    Country: '',
    FIName: '',
    FINameFrench: '',
    FinancialInstitutionID: '',
    FinancialInstitutionNumber: '',
    ExternalFIID: '',
    VIOFinancialInstitutionsFiserv_ID: '',
    HasMoreThanOne: false,
    OnlineConnectionEnabled: '1',
  },
  MainFinancialInstitution: {
    AssociatedFINames: '',
    AssociatedFINamesFrench: '',
    AssociatedExternalFIIDs: '',
    ColorCode: '',
    Country: '',
    FIName: '',
    FINameFrench: '',
    FinancialInstitutionID: '',
    FinancialInstitutionNumber: '',
    ExternalFIID: '',
    VIOFinancialInstitutionsFiserv_ID: '',
    HasMoreThanOne: false,
    OnlineConnectionEnabled: '1',
  },
  BankingType: 'online',
  AccountType: 'personal',
  SubFiniancialIntistutions: [],
  BankAccountRail: undefined,
  reset: () => {},
  setFinancialInstitution: () => {},
  setMainFinancialInstitution: () => {},
  setBankingType: () => {},
  setSubFiniancialIntistutions: () => {},
  setAccountType: () => {},
  setBankAccountRail: () => {},
});
export const BankProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const storage = useStorage();
  const { bankInfo, bankType, subBanks, accountType, mainBankInfo, bankAccountRail } = storage.bank.getBank();
  return (
    <BankContext.Provider
      value={{
        FinancialInstitution: bankInfo,
        MainFinancialInstitution: mainBankInfo,
        BankingType: bankType,
        SubFiniancialIntistutions: subBanks,
        AccountType: accountType,
        BankAccountRail: bankAccountRail,
        reset: () => storage.bank.clearBank,
        setFinancialInstitution: storage.bank.storeBank,
        setMainFinancialInstitution: storage.bank.storeMainBank,
        setBankingType: storage.bank.storeBankType,
        setSubFiniancialIntistutions: storage.bank.storeBankSubBanks,
        setAccountType: storage.bank.storeAccountType,
        setBankAccountRail: storage.bank.storeBankAccountRail,
      }}
    >
      {children}
    </BankContext.Provider>
  );
};
export const useBank = (): BankCtx => React.useContext(BankContext);
